import * as THREE from "three";

/**
 * 건물 벽면 패턴 타입
 */
export type WallPattern = "vertical" | "horizontal" | "stripes";

/**
 * 건물 셰이더 머티리얼 옵션
 */
export interface BuildingShaderOptions {
  /** 유저 컬러 (hex string, e.g. "#ff0000") */
  userColor: string;
  /** 지붕 노이즈 강도 (0.0 ~ 0.3) */
  noiseIntensity?: number;
  /** 벽면 패턴 타입 */
  wallPattern?: WallPattern;
  /** 벽면 패턴 강도 (0.0 ~ 0.3) */
  patternIntensity?: number;
  /** 투명도 (0.0 ~ 1.0) */
  opacity?: number;
  /** 양면 렌더링 여부 (폭발 애니메이션용) */
  doubleSide?: boolean;
  /** 뒷면 어둡기 배율 (0.0 = 동일, 1.0 = 완전 어둡게) */
  backFaceDarkness?: number;
  /** 지붕 텍스처 (정해지면 노이즈 대신 텍스처 사용) */
  roofTexture?: THREE.Texture | null;
  /** 건물별 랜덤 시드 (창문 크기/형태 결정) */
  buildingSeed?: number;
}

/**
 * Vertex Shader
 */
const vertexShader = `
varying vec3 vNormal;
varying vec3 vLocalPosition;
varying vec3 vWorldPosition;
varying vec4 vGroundClipPos;

void main() {
  vNormal = normalize(normalMatrix * normal);
  vLocalPosition = position;
  
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos.xyz;
  
  vec4 groundPos = vec4(position.x, 0.0, position.z, 1.0);
  vGroundClipPos = projectionMatrix * modelViewMatrix * groundPos;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

/**
 * Fragment Shader
 */
const fragmentShader = `
uniform vec3 userColor;
uniform float noiseIntensity;
uniform int wallPattern;
uniform float patternIntensity;
uniform float opacity;
uniform float backFaceDarkness;
uniform sampler2D roofTexture;
uniform float hasRoofTexture;
uniform float uBuildingSeed;  // 건물별 랜덤 시드

// GUI 클리핑용 uniforms
uniform mat4 uViewProjectionMatrix;
uniform float uGUIControlPoint;
uniform vec2 uWorldXDir;
uniform vec2 uWorldZDir;
uniform float uEnableGUIClipping;
uniform float uGUIClippingDebug;
uniform float uTopLeftEnabled;
uniform float uTopRightEnabled;
uniform float uBottomLeftEnabled;
uniform float uBottomRightEnabled;
uniform float uTopLeftOffset;
uniform float uTopRightOffset;
uniform float uBottomLeftOffset;
uniform float uBottomRightOffset;

varying vec3 vNormal;
varying vec3 vLocalPosition;
varying vec3 vWorldPosition;
varying vec4 vGroundClipPos;

// 노이즈 함수
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

// 파스텔 톤 변환
vec3 toPastel(vec3 color, float amount) {
  return mix(color, vec3(1.0), amount);
}

// 2단계 툰 셰이딩
float toonShade2Step(float ndotl) {
  return ndotl > 0.0 ? 1.0 : 0.75;
}

// 창문 패턴
float windowPattern(vec2 uv) {
  float windowSize = 0.35;
  float gap = 0.2;
  float cellSize = windowSize + gap;
  vec2 cellUV = mod(uv, cellSize);
  float halfGap = gap * 0.5;
  bool inWindow = cellUV.x > halfGap && cellUV.x < (halfGap + windowSize) &&
                  cellUV.y > halfGap && cellUV.y < (halfGap + windowSize);
  return inWindow ? 1.0 : 0.0;
}

// 창문 색상 (랜덤 불빛) - buildingSeed를 파라미터로 받음
vec3 getWindowColor(vec2 uv, vec3 wallColor, float buildingSeed) {
  float windowSize = 0.35;
  float gap = 0.2;
  float cellSize = windowSize + gap;
  vec2 cellId = floor(uv / cellSize);
  
  // 건물 시드를 cellId에 추가하여 건물마다 다른 불빛 패턴
  vec2 seedOffset = vec2(buildingSeed * 0.01, buildingSeed * 0.007);
  float windowHash = hash(cellId + seedOffset);
  float litHash = hash(cellId + seedOffset + vec2(42.0, 13.0));
  
  vec3 darkWindow = wallColor * 0.2;
  float lit = step(0.65, litHash);
  
  vec3 litColor = mix(
    vec3(1.0, 0.92, 0.65),
    vec3(1.0, 0.75, 0.45),
    windowHash
  );
  
  return mix(darkWindow, litColor, lit);
}

// GUI 삼각형 체크
float sideOfLine(vec2 linePoint, vec2 lineDir, vec2 point) {
  vec2 perp = vec2(-lineDir.y, lineDir.x);
  return dot(point - linePoint, perp);
}

int whichGUITriangle(vec2 ndc) {
  float controlX = uGUIControlPoint * 2.0 - 1.0;
  
  // TopLeft
  vec2 topLeftCorner = vec2(-1.0, 1.0);
  vec2 baseTopLeftControl = vec2(controlX, 1.0);
  vec2 topLeftPerp = normalize(vec2(uWorldZDir.y, -uWorldZDir.x));
  float topLeftDistToCorner = length(baseTopLeftControl - topLeftCorner);
  vec2 topLeftControl = baseTopLeftControl + topLeftPerp * uTopLeftOffset * topLeftDistToCorner * 1.5;
  float sideTopLeft = sideOfLine(topLeftControl, uWorldZDir, ndc);
  bool inTopLeft = sideTopLeft < 0.0 && uTopLeftEnabled > 0.5;
  
  // TopRight
  vec2 topRightCorner = vec2(1.0, 1.0);
  vec2 baseTopRightControl = vec2(controlX, 1.0);
  vec2 topRightPerp = normalize(vec2(-uWorldXDir.y, uWorldXDir.x));
  float topRightDistToCorner = length(baseTopRightControl - topRightCorner);
  vec2 topRightControl = baseTopRightControl + topRightPerp * uTopRightOffset * topRightDistToCorner * 1.5;
  float sideTopRight = sideOfLine(topRightControl, uWorldXDir, ndc);
  bool inTopRight = sideTopRight > 0.0 && uTopRightEnabled > 0.5;
  
  // BottomLeft
  vec2 bottomLeftCorner = vec2(-1.0, -1.0);
  vec2 baseBottomLeftControl = vec2(controlX, -1.0);
  vec2 bottomLeftPerp = normalize(vec2(uWorldXDir.y, -uWorldXDir.x));
  float bottomLeftDistToCorner = length(baseBottomLeftControl - bottomLeftCorner);
  vec2 bottomLeftControl = baseBottomLeftControl + bottomLeftPerp * uBottomLeftOffset * bottomLeftDistToCorner * 1.5;
  float sideBottomLeft = sideOfLine(bottomLeftControl, uWorldXDir, ndc);
  bool inBottomLeft = sideBottomLeft < 0.0 && uBottomLeftEnabled > 0.5;
  
  // BottomRight
  vec2 bottomRightCorner = vec2(1.0, -1.0);
  vec2 baseBottomRightControl = vec2(controlX, -1.0);
  vec2 bottomRightPerp = normalize(vec2(-uWorldZDir.y, uWorldZDir.x));
  float bottomRightDistToCorner = length(baseBottomRightControl - bottomRightCorner);
  vec2 bottomRightControl = baseBottomRightControl + bottomRightPerp * uBottomRightOffset * bottomRightDistToCorner * 1.5;
  float sideBottomRight = sideOfLine(bottomRightControl, uWorldZDir, ndc);
  bool inBottomRight = sideBottomRight > 0.0 && uBottomRightEnabled > 0.5;
  
  if (inTopLeft) return 1;
  if (inTopRight) return 2;
  if (inBottomLeft) return 3;
  if (inBottomRight) return 4;
  return 0;
}

void main() {
  // GUI 클리핑
  if (uEnableGUIClipping > 0.5) {
    vec2 ndc = vGroundClipPos.xy / vGroundClipPos.w;
    int tri = whichGUITriangle(ndc);
    
    if (tri > 0) {
      if (uGUIClippingDebug > 0.5) {
        if (tri == 1) gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
        else if (tri == 2) gl_FragColor = vec4(0.0, 1.0, 0.0, 1.0);
        else if (tri == 3) gl_FragColor = vec4(0.0, 0.0, 1.0, 1.0);
        else gl_FragColor = vec4(1.0, 1.0, 0.0, 1.0);
        return;
      }
      discard;
    }
  }
  
  vec3 pastelColor = toPastel(userColor, 0.7);
  vec3 vibrantColor = toPastel(userColor, 0.5);
  
  // 조명 방향
  vec3 lightDir = normalize(vec3(0.5, 1.0, 0.8));
  float ndotl = dot(vNormal, lightDir);
  float toonBrightness = toonShade2Step(ndotl);
  
  vec3 finalColor = pastelColor;
  
  // 지붕 (Y+)
  if (vNormal.y > 0.5) {
    if (hasRoofTexture > 0.5) {
      vec2 roofUV = vLocalPosition.xz * 0.5;
      vec3 texColor = texture2D(roofTexture, roofUV).rgb;
      finalColor = mix(vibrantColor * 0.9, texColor * vibrantColor, 0.5);
    } else {
      float n = noise(vLocalPosition.xz * 25.0);
      float effectValue = (n - 0.5) * noiseIntensity * 1.2;
      finalColor = vibrantColor * (1.0 + effectValue);
    }
  }
  // 옆벽 (X+): 창문 패턴 + 랜덤 불빛
  else if (vNormal.x > 0.5) {
    vec2 wallUV = vec2(vLocalPosition.z, vLocalPosition.y);
    float isWindow = windowPattern(wallUV);
    
    vec3 wallColor = vibrantColor * vec3(0.95, 0.95, 1.0);
    wallColor *= (0.8 + 0.2 * toonBrightness);
    
    vec3 windowColor = getWindowColor(wallUV, wallColor, uBuildingSeed);
    finalColor = mix(wallColor, windowColor, isWindow);
  }
  // 반대 옆벽 (X-): 창문 없음
  else if (vNormal.x < -0.5) {
    vec3 wallColor = vibrantColor * vec3(0.95, 0.95, 1.0) * 0.9;
    wallColor *= (0.8 + 0.2 * toonBrightness);
    finalColor = wallColor;
  }
  // 앞벽 (Z+): 창문 패턴 + 랜덤 불빛
  else if (vNormal.z > 0.5) {
    vec2 wallUV = vec2(vLocalPosition.x, vLocalPosition.y);
    float isWindow = windowPattern(wallUV);
    
    vec3 wallColor = vibrantColor * vec3(1.0, 0.98, 0.96);
    wallColor *= (0.85 + 0.15 * toonBrightness);
    
    vec3 windowColor = getWindowColor(wallUV, wallColor, uBuildingSeed);
    finalColor = mix(wallColor, windowColor, isWindow);
  }
  // 뒷벽 (Z-): 어두운 면
  else if (vNormal.z < -0.5) {
    finalColor = vibrantColor * 0.8;
  }
  // 바닥 (Y-)
  else if (vNormal.y < -0.5) {
    finalColor = vibrantColor * 0.6;
  }
  // 기타
  else {
    finalColor = vibrantColor * 0.95;
  }
  
  finalColor = max(finalColor, pastelColor * 0.25);
  finalColor = clamp(finalColor, 0.0, 1.0);
  
  // 뒷면 처리
  if (!gl_FrontFacing) {
    finalColor *= (1.0 - backFaceDarkness * 0.3);
  }
  
  gl_FragColor = vec4(finalColor, opacity);
}
`;

/**
 * 벽면 패턴을 숫자로 변환
 */
function patternToInt(pattern: WallPattern): number {
  switch (pattern) {
    case "vertical":
      return 0;
    case "horizontal":
      return 1;
    case "stripes":
      return 2;
    default:
      return 0;
  }
}

/**
 * Hex 색상을 THREE.Color로 변환
 */
function hexToColor(hex: string): THREE.Color {
  return new THREE.Color(hex);
}

/**
 * 건물용 커스텀 ShaderMaterial 생성
 */
export function createBuildingShaderMaterial(
  options: BuildingShaderOptions
): THREE.ShaderMaterial {
  const {
    userColor,
    noiseIntensity = 0.15,
    wallPattern = "vertical",
    patternIntensity = 0.2,
    opacity = 1.0,
    doubleSide = false,
    backFaceDarkness = 0.3,
    buildingSeed = Math.random() * 1000,
  } = options;

  const material = new THREE.ShaderMaterial({
    uniforms: {
      userColor: { value: hexToColor(userColor) },
      noiseIntensity: { value: noiseIntensity },
      wallPattern: { value: patternToInt(wallPattern) },
      patternIntensity: { value: patternIntensity },
      opacity: { value: opacity },
      backFaceDarkness: { value: backFaceDarkness },
      roofTexture: { value: null },
      hasRoofTexture: { value: 0.0 },
      uBuildingSeed: { value: buildingSeed },
      // GUI 클리핑용 uniforms
      uViewProjectionMatrix: { value: new THREE.Matrix4() },
      uGUIControlPoint: { value: 0.5 },
      uWorldXDir: { value: new THREE.Vector2(0.866, -0.5) },
      uWorldZDir: { value: new THREE.Vector2(-0.866, -0.5) },
      uEnableGUIClipping: { value: 0.0 },
      uGUIClippingDebug: { value: 0.0 },
      uTopLeftEnabled: { value: 1.0 },
      uTopRightEnabled: { value: 1.0 },
      uBottomLeftEnabled: { value: 1.0 },
      uBottomRightEnabled: { value: 1.0 },
      uTopLeftOffset: { value: 0.0 },
      uTopRightOffset: { value: 0.0 },
      uBottomLeftOffset: { value: 0.0 },
      uBottomRightOffset: { value: 0.0 },
    },
    vertexShader,
    fragmentShader,
    transparent: true,
    side: doubleSide ? THREE.DoubleSide : THREE.FrontSide,
  });

  return material;
}

/**
 * 기존 머티리얼의 uniforms 업데이트
 */
export function updateBuildingShaderUniforms(
  material: THREE.ShaderMaterial,
  updates: Partial<BuildingShaderOptions>
): void {
  if (updates.userColor !== undefined) {
    material.uniforms.userColor.value = hexToColor(updates.userColor);
  }
  if (updates.noiseIntensity !== undefined) {
    material.uniforms.noiseIntensity.value = updates.noiseIntensity;
  }
  if (updates.wallPattern !== undefined) {
    material.uniforms.wallPattern.value = patternToInt(updates.wallPattern);
  }
  if (updates.patternIntensity !== undefined) {
    material.uniforms.patternIntensity.value = updates.patternIntensity;
  }
  if (updates.opacity !== undefined) {
    material.uniforms.opacity.value = updates.opacity;
  }
}

/**
 * GUI 클리핑용 uniforms 업데이트
 */
export interface GUIClippingParams {
  viewProjectionMatrix: THREE.Matrix4;
  controlPoint: number;
  worldXDir: THREE.Vector2;
  worldZDir: THREE.Vector2;
  enabled: boolean;
}

export function updateGUIClippingUniforms(
  material: THREE.ShaderMaterial,
  params: GUIClippingParams
): void {
  if (!material.uniforms.uViewProjectionMatrix) return;

  material.uniforms.uViewProjectionMatrix.value.copy(
    params.viewProjectionMatrix
  );
  material.uniforms.uGUIControlPoint.value = params.controlPoint;
  material.uniforms.uWorldXDir.value.copy(params.worldXDir);
  material.uniforms.uWorldZDir.value.copy(params.worldZDir);
  material.uniforms.uEnableGUIClipping.value = params.enabled ? 1.0 : 0.0;
}
